import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  loadWidgetApi,
  MixcloudWidget,
  widgetIframeUrl,
} from '../../services/mixcloud/mixcloudWidget';
import useBlurWhenHidden from '../../services/shortcuts/useBlurWhenHidden';
import { seekTarget } from '../player/seek';

/**
 * What the player asks for. A new object every time the play effect runs, so
 * that pressing play on the show already loaded starts it again - the key on
 * its own would not have changed.
 */
export interface MixcloudPlayRequest {
  /** The cloudcast key, `/user/slug/`. */
  key: string;
  /**
   * Where to pick the show up, for one restored from the last session.
   * Nothing sets it for an ordinary play.
   */
  startSeconds?: number;
  /**
   * Whether to start playing once the show is loaded. Default true; false is
   * what a restored session asks for - a two-hour set resuming at the
   * ninety-minute mark on its own, because someone reopened a tab, is not a
   * welcome.
   */
  autoplay?: boolean;
}

/**
 * Applies the position a restored session asked for, once, to a show that is
 * now playing.
 *
 * It waits for playback because a seek before that does not survive.
 * Measured: seeking a widget that has never played answers true and moves
 * the scrubber, and then the show finishes loading and the playhead is back
 * at zero - and pressing play starts from the top regardless. A seek within
 * a playing show holds. So the price of resuming a Mixcloud show is a
 * visible jump from zero when it starts, and the alternative is not
 * resuming it.
 */
function resumePendingSeek(
  widget: MixcloudWidget,
  pending: React.MutableRefObject<number | undefined>,
) {
  const seconds = pending.current;

  if (!seconds) {
    return;
  }

  pending.current = undefined;

  widget.seek(seconds).catch(console.debug);
}

/**
 * What the player can ask of the widget from outside. The transport for a
 * Mixcloud show is Mixcloud's own bar, so this is only what the keyboard
 * shortcuts need - see usePlayPauseShortcut and usePlayerShortcuts.
 */
export interface MixcloudPlayerHandle {
  /** Play if paused, pause if playing. Nothing at all before a show loads. */
  togglePlay(): void;
  /** Moves the playhead by this many seconds. Nothing at all before a show loads. */
  seekBy(seconds: number): void;
}

interface Props {
  className?: string;
  /**
   * True while another source is playing and `className` hides the frame.
   * Said separately because a hidden frame that still has focus keeps the
   * keyboard from the page - see useBlurWhenHidden.
   */
  hidden?: boolean;
  /** Set while a Mixcloud show is the current track, undefined otherwise. */
  request?: MixcloudPlayRequest;
  /** The show finished on its own. */
  onEnded(): void;
  /** Audio is actually coming out, which ends a run of failures. */
  onPlaying(): void;
  /** The widget could not play this show. */
  onError(reason: string): void;
  /**
   * How far into the show the widget is, a few times a second. The widget is
   * the only thing that knows: there is no audio element to ask.
   */
  onProgress(positionMs: number, durationMs: number): void;
}

/**
 * The Mixcloud half of the player: one iframe, kept for the app's lifetime,
 * hidden when something else is playing.
 *
 * It shows Mixcloud's own mini widget rather than Fadila's transport, which
 * is not laziness on the layout's part - the widget is where Mixcloud's play
 * count, its branding and its link back to the show live, and it is the only
 * thing on the page that knows how far into a two-hour set you are. The
 * player's own next/previous still work, because those change the track
 * rather than the playhead.
 *
 * Why the iframe is never unmounted: widgetApi.js adds a window message
 * listener per widget and offers no way to remove it, and rebuilding the API
 * after a fresh `src` silently drops the event handlers registered here
 * (see mixcloudWidget.ts). One element, one widget, `load()` for every
 * change of show.
 *
 * The one rule of the widget API is that the widget must be visible for it
 * to work, which is why this renders nothing at all until the first Mixcloud
 * track: an iframe hidden from birth would be a handshake with a frame the
 * browser never painted. After that it is only ever hidden while another
 * source plays, and nothing is asked of it then.
 */
const MixcloudPlayerWrapper = React.forwardRef<MixcloudPlayerHandle, Props>(
  function MixcloudPlayerWrapper(
    { className, hidden = false, request, onEnded, onPlaying, onError, onProgress }: Props,
    ref,
  ) {
    const iframeEl = useRef<HTMLIFrameElement>(null);

    useBlurWhenHidden(iframeEl, hidden);
    const widgetRef = useRef<MixcloudWidget>(undefined);

    /**
     * Where a restored show should pick up, until the widget is playing and
     * will take it. See resumePendingSeek.
     */
    const pendingSeekRef = useRef<number>(undefined);

    /**
     * Set once, by the first show played. After that the src stays put and
     * `load()` does the work, so this doubles as "has the iframe been born".
     */
    const [src, setSrc] = useState<string>();

    // The handlers change identity on every render of the player; the widget's
    // are registered once. A ref is what lets one keep up with the other.
    const handlersRef = useRef({ onEnded, onPlaying, onError, onProgress });
    handlersRef.current = { onEnded, onPlaying, onError, onProgress };

    useEffect(
      function attachWidget() {
        if (!src || !iframeEl.current || widgetRef.current) {
          return;
        }

        const iframe = iframeEl.current;
        let halt = false;

        loadWidgetApi()
          .then(
            function createWidget(mixcloud) {
              if (halt || widgetRef.current) {
                return;
              }

              const widget = mixcloud.PlayerWidget(iframe);

              widgetRef.current = widget;

              /*
        After `ready`, and that is not tidiness: a fresh PlayerWidget is
        `{ ready, events: {} }`. Every event is created when the widget
        answers with its own API description - the same message that resolves
        `ready` - so `events.play.on(...)` before then reads `on` of
        undefined, and one throw in this callback takes every registration
        after it with it.

        Which is what used to happen, silently, the throw going nowhere but an
        unhandled rejection: no handler was ever attached, so a Mixcloud show
        never reported that it had started, never reported an error, and never
        announced its own end - which is why the player sat there at the end of
        a show instead of moving on.
      */
              return widget.ready.then(function registerHandlers() {
                if (halt) {
                  return;
                }

                widget.events.play.on(() => {
                  // The show has just started from the top, whatever the scrubber
                  // was showing. This is the moment a seek survives, so it is the
                  // moment a restored session actually resumes.
                  resumePendingSeek(widget, pendingSeekRef);

                  handlersRef.current.onPlaying();
                });
                widget.events.ended.on(() => handlersRef.current.onEnded());
                widget.events.progress.on((position, duration) =>
                  handlersRef.current.onProgress(position * 1000, duration * 1000),
                );
                widget.events.error.on((err) => {
                  console.debug('Mixcloud widget error', err);

                  // Never flags the track. A widget error is as often the embed, the
                  // network or a show the uploader has made unavailable to embed as it
                  // is the show being gone.
                  handlersRef.current.onError('mixcloud:widget');
                });
              });
            },
            (err) => {
              console.debug(err);
              handlersRef.current.onError('mixcloud:script');
            },
          )
          .catch((err) => {
            console.debug('Mixcloud widget setup failed', err);
            handlersRef.current.onError('mixcloud:widget');
          });

        return function cancel() {
          halt = true;
        };
      },
      [src],
    );

    useEffect(
      function playRequested() {
        if (!request) {
          // Switched to another source. The widget keeps the show it had, so
          // that coming back does not reload the iframe; it just stops.
          const widget = widgetRef.current;

          if (widget) {
            widget.ready.then(() => widget.pause()).catch(console.debug);
          }

          return;
        }

        // Parked rather than seeked inline, whichever path loads the show: the
        // widget may not be in a position to take a seek yet. See
        // applyPendingSeek.
        pendingSeekRef.current = request.startSeconds;

        if (!src) {
          // First show of the session: the iframe is born pointing at it and
          // starts on its own, which is one round trip fewer than loading an
          // empty widget and calling load(). A resumed show is born paused
          // instead, and the seek follows once the widget answers.
          setSrc(widgetIframeUrl(request.key, request.autoplay !== false));

          return;
        }

        const widget = widgetRef.current;

        if (!widget) {
          // The script is still loading. attachWidget resolves in a moment and
          // the iframe is already pointing at a show; the only case that lands
          // here is a second show chosen within that moment, and the effect runs
          // again for it once the widget exists.
          return;
        }

        let halt = false;

        widget.ready
          .then(() => {
            if (!halt) {
              return widget.load(request.key, request.autoplay !== false);
            }
          })
          .catch((err) => {
            console.debug(err);
            handlersRef.current.onError('mixcloud:load');
          });

        return function cancel() {
          halt = true;
        };
        // src is deliberately not a dependency: it changes exactly once, from
        // this effect, and reacting to that would load the first show twice.
      },
      [request],
    );

    useImperativeHandle(
      ref,
      () => ({
        togglePlay() {
          const widget = widgetRef.current;

          if (!widget) {
            return;
          }

          // Through `ready` because the method itself does not exist before the
          // widget has described its API - the same trap the handlers above are
          // registered around.
          widget.ready.then(() => widget.togglePlay()).catch(console.debug);
        },
        seekBy(seconds: number) {
          const widget = widgetRef.current;

          if (!widget) {
            return;
          }

          // A seek only holds in a show that is playing (see resumePendingSeek);
          // asked of one that is not, the widget answers and does nothing, which
          // is the right amount of nothing.
          widget.ready
            .then(() => Promise.all([widget.getPosition(), widget.getDuration()]))
            .then(([position, duration]) => widget.seek(seekTarget(position, seconds, duration)))
            .catch(console.debug);
        },
      }),
      [],
    );

    // Nothing to show until the widget has a show. See the class comment: the
    // API needs a visible frame, so it is not created before there is one.
    if (!src) {
      return null;
    }

    return (
      <iframe
        ref={iframeEl}
        title="mixcloud-widget"
        className={className}
        src={src}
        width="100%"
        height="60"
        frameBorder="0"
        /*
        The widget is a cross-origin frame, so starting playback from
        load(…, true) is only allowed if this page delegates the permission -
        the same reason the YouTube embed carries it.
      */
        allow="autoplay"
      />
    );
  },
);

export default MixcloudPlayerWrapper;
